// RelationshipsPanel.tsx 
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Select, MenuItem, InputLabel, FormControl, IconButton, Tooltip, TextField,
  useTheme, FormControlLabel, Switch,
  Menu
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import FilterAltIcon  from "@mui/icons-material/Filter";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import SafeAgGrid from "./SafeAgGrid";
import { Table, TableRelationship, TableField, TablePage } from "../types/interfaces";
import API from "../api/axiosInstance";
import { createUUID } from "../utils/validators";
import SearchDialog from "./SearchDialog";

interface Props {
  table: Table;
  setTable: React.Dispatch<React.SetStateAction<Table>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const RelationshipsPanel: React.FC<Props> = ({ table, setTable, setIsDirty }) => {
  const theme = useTheme();
  const [relationships, setRelationships] = useState<TableRelationship[]>([]);
  const [filteredRelationships, setFilteredRelationships] = useState<TableRelationship[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TableRelationship | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoRelationship, setInfoRelationship] = useState<TableRelationship | null>(null);
  const [allTables, setAllTables] = useState<Table[]>([]);
  const [fieldOptions, setFieldOptions] = useState<TableField[]>([]);
  const [childFieldOptions, setChildFieldOptions] = useState<TableField[]>([]);
  const [childPages, setChildPages] = useState<TablePage[]>([]);
  const fieldCache = useRef<{ [tableId: string]: TableField[] }>({});
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchDialogTargetRel, setSearchDialogTargetRel] = useState<TableRelationship | null>(null);
  
  useEffect(() => {
    setRelationships(Array.isArray(table.relationships) ? table.relationships : []);
  }, [table.relationships]);

  useEffect(() => {
    fetchAllTables();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [relationships, showDeleted, searchText]);

  const fetchAllTables = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || "{}");
      const headers = { headers: { authToken: user.user?.authtoken, sessionId: user.user?.sessionId } };
      const res = await API.get("/tables", headers);
      setAllTables(res.data);
    } catch (error) {
      console.error("Failed to fetch tables:", error);
    }
  };

  const fetchTableDetails = async (tableId: string): Promise<{ fields: TableField[]; pages: TablePage[] }> => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || "{}");
      const headers = {
        headers: {
          authToken: user.user?.authtoken,
          sessionId: user.user?.sessionId,
        },
      };
      const res = await API.get(`/tables/${tableId}`, headers);
      const fields = Array.isArray(res.data?.fields) ? res.data.fields : [];
      const pages = Array.isArray(res.data?.pages) ? res.data.pages : [];
      fieldCache.current[tableId] = fields;
      return { fields, pages };
    } catch (error) {
      console.error(`Failed to fetch table ${tableId} details`, error);
      return { fields: [], pages: [] };
    }
  };
  

  const handleChildTableChange = async (childTableID: string) => {
    if (!editing) return;
    const { fields: childFields, pages } = await fetchTableDetails(childTableID);
    const defaultAdd = pages.find(p => p.pageType?.toUpperCase().includes("ADD"));
    const defaultEdit = pages.find(p => p.pageType?.toUpperCase().includes("EDIT"));
    const defaultView = pages.find(p => p.pageType?.toUpperCase().includes("VIEW"));
  
    setEditing(prev => prev ? {
      ...prev,
      childTableID,
      linkedField: "",
      addPageID: defaultAdd?.pageID,
      editPageID: defaultEdit?.pageID,
      viewPageID: defaultView?.pageID,
    } : null);
  
    setChildFieldOptions(childFields);
    setChildPages(pages);
  };
  
  
  const openModal = async (existing?: TableRelationship) => {
    const newItem: TableRelationship = existing || {
      relationshipID: createUUID(),
      parentTableID: table.tableID,
      childTableID: "",
      relationshipType: "ONE_TO_MANY",
      foreignKeyField: "",
      linkedField: "",
      allowPopupAdd: false,
      allowPopupEdit: false,
      allowPopupView: false,
      createdAt: new Date().toISOString(),
      deleted: false,
    };
  
    setEditing(newItem);
    const { fields: parentFields } = await fetchTableDetails(table.tableID);
    setFieldOptions(parentFields);
  
    if (newItem.childTableID) {
      const { fields: childFields, pages } = await fetchTableDetails(newItem.childTableID);
      setChildFieldOptions(childFields);
      setChildPages(pages);
    } else {
      setChildFieldOptions([]);
      setChildPages([]);
    }
  
    setShowModal(true);
  };
  

    const closeModal = () => {
    setEditing(null);
    setShowModal(false);
  };

  const handleSave = () => {
    if (!editing?.childTableID || !editing.foreignKeyField || !editing.linkedField) {
      alert("All fields are required.");
      return;
    }

    const updated = [...relationships];
    const existingIndex = updated.findIndex(r => r.relationshipID === editing.relationshipID);
    const linkedFieldObj = childFieldOptions.find(f => f.fieldName === editing.linkedField);
    if (linkedFieldObj) {
      editing.linkedField = linkedFieldObj.fieldID;
    }

    if (existingIndex >= 0) {
      updated[existingIndex] = editing;
    } else {
      updated.push(editing);
    }

    setRelationships(updated);
    setTable(prev => ({ ...prev, relationships: updated }));
    setIsDirty(true);
    closeModal();
  };

  const handleDelete = (id: string) => {
    const updated = relationships.map(r => r.relationshipID === id ? { ...r, deleted: true } : r);
    setRelationships(updated);
    setTable(prev => ({ ...prev, relationships: updated }));
    setIsDirty(true);
  };

  const handleRestore = (id: string) => {
    const updated = relationships.map(r => r.relationshipID === id ? { ...r, deleted: false } : r);
    setRelationships(updated);
    setTable(prev => ({ ...prev, relationships: updated }));
    setIsDirty(true);
  };

  const applyFilters = () => {
    let filtered = [...relationships];
    if (!showDeleted) filtered = filtered.filter(r => !r.deleted);
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(r =>
        r.foreignKeyField?.toLowerCase().includes(lower) ||
        r.childTableID?.toLowerCase().includes(lower) ||
        r.linkedField?.toLowerCase().includes(lower)
      );
    }
    setFilteredRelationships(filtered);
  };

  const fieldNameById = (tableId: string, fieldId: string) => {
    const allFields = fieldCache.current[tableId] || [];
    return allFields.find(f => f.fieldID === fieldId)?.fieldName || fieldId;
  };

  const ActionsCell: React.FC<{ data: TableRelationship }> = ({ data }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
  
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => setAnchorEl(null);
  
    return (
      <>
        <Tooltip title="Options">
          <IconButton aria-label="Actions" size="small" onClick={handleOpen}>
            <span className="material-icons">settings</span>
          </IconButton>
        </Tooltip>
        <Menu
          id={`actions-menu-${data.relationshipID}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {!data.deleted && (
            <>
              <MenuItem onClick={() => { handleClose(); openModal(data); }}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); handleDelete(data.relationshipID); }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" /> Delete
              </MenuItem>
            </>
          )}
          {data.deleted && (
            <MenuItem onClick={() => { handleClose(); handleRestore(data.relationshipID); }}>
              <RestoreIcon fontSize="small" sx={{ mr: 1 }} color="success" /> Restore
            </MenuItem>
          )}
          <MenuItem onClick={() => { handleClose(); setInfoRelationship(data); setInfoDialogOpen(true); }}>
            <InfoIcon fontSize="small" sx={{ mr: 1 }} /> Info
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); setSearchDialogTargetRel(data); setSearchDialogOpen(true); }}>
            <FilterAltIcon fontSize="small" sx={{ mr: 1 }} /> Filters
          </MenuItem>
        </Menu>
      </>
    );
  };
  

  const columnDefs: ColDef<TableRelationship>[] = [
    { field: "foreignKeyField", headerName: "Key Field", flex: 1 },
    {
      field: "childTableID", headerName: "Child Table", flex: 1,
      valueFormatter: ({ value }) => allTables.find(t => t.tableID === value)?.tableName || value
    },
    {
      field: "linkedField",
      headerName: "Linked Field",
      flex: 1,
      valueFormatter: (params) => {
        const relationship = params.data;
        if (!relationship?.childTableID || !relationship?.linkedField) return params.value;
        const fieldList = fieldCache.current[relationship.childTableID] || [];
        return fieldList.find(f => f.fieldID === relationship.linkedField)?.fieldName || params.value;
      }
    },
    {
      headerName: "Actions",
      colId: "actions",
      width: 100,
      cellRenderer: (params: ICellRendererParams<TableRelationship>) => {
        const { data } = params;
        if (!data) return null;
    
        return <ActionsCell data={data} />;
      }
    }
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Relationships</Typography>
        <Button variant="contained" onClick={() => openModal()}>+ Add Relationship</Button>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <FormControlLabel control={<Switch checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />} label="Show Deleted" />
      </Box>

      <SafeAgGrid columnDefs={columnDefs} rowData={filteredRelationships} enableQuickSearch loading={!columnDefs.length} />
      {filteredRelationships.map((rel) => (
        <Menu
          key={rel.relationshipID}
          id={`actions-menu-${rel.relationshipID}`}
          anchorEl={null}
          open={false}
          onClose={() => {}}
          MenuListProps={{ 'aria-labelledby': `actions-button-${rel.relationshipID}` }}
        >
          {!rel.deleted ? (
            <>
              <MenuItem onClick={() => openModal(rel)}>Edit</MenuItem>
              <MenuItem onClick={() => handleDelete(rel.relationshipID)}>Delete</MenuItem>
            </>
          ) : (
            <MenuItem onClick={() => handleRestore(rel.relationshipID)}>Restore</MenuItem>
          )}
        </Menu>
      ))}

      {/* Edit/Add Modal */}
{editing && (
  <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
    <DialogTitle>{editing.relationshipID ? "Edit" : "Add"} Relationship</DialogTitle>
    <DialogContent dividers>
      <FormControl fullWidth margin="dense">
        <InputLabel>Key Field</InputLabel>
        <Select
          value={editing.foreignKeyField}
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, foreignKeyField: e.target.value } : null
            )
          }
          label="Key Field"
        >
          {fieldOptions.map((field) => (
            <MenuItem key={field.fieldID} value={field.fieldName}>
              {field.fieldName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <InputLabel>Child Table</InputLabel>
        <Select
          value={editing.childTableID}
          onChange={(e) => handleChildTableChange(e.target.value)}
          label="Child Table"
        >
          {allTables.map((tbl) => (
            <MenuItem key={tbl.tableID} value={tbl.tableID}>
              {tbl.tableName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <InputLabel>Linked Field</InputLabel>
        <Select
          value={
            childFieldOptions.find((f) => f.fieldID === editing.linkedField)
              ?.fieldName || editing.linkedField
          }
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, linkedField: e.target.value } : null
            )
          }
          label="Linked Field"
        >
          {childFieldOptions.map((field) => (
            <MenuItem key={field.fieldID} value={field.fieldName}>
              {field.fieldName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <InputLabel>Add Page</InputLabel>
        <Select
          value={editing.addPageID || ""}
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, addPageID: e.target.value || undefined } : null
            )
          }
          label="Add Page"
        >
          <MenuItem value="">None</MenuItem>
          {childPages
            .filter((p) => p.pageType.toUpperCase().includes("ADD"))
            .map((p) => (
              <MenuItem key={p.pageID} value={p.pageID}>
                {p.pageName}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <InputLabel>Edit Page</InputLabel>
        <Select
          value={editing.editPageID || ""}
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, editPageID: e.target.value || undefined } : null
            )
          }
          label="Edit Page"
        >
          <MenuItem value="">None</MenuItem>
          {childPages
            .filter((p) => p.pageType.toUpperCase().includes("EDIT"))
            .map((p) => (
              <MenuItem key={p.pageID} value={p.pageID}>
                {p.pageName}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <InputLabel>View Page</InputLabel>
        <Select
          value={editing.viewPageID || ""}
          onChange={(e) =>
            setEditing((prev) =>
              prev ? { ...prev, viewPageID: e.target.value || undefined } : null
            )
          }
          label="View Page"
        >
          <MenuItem value="">None</MenuItem>
          {childPages
            .filter((p) => p.pageType.toUpperCase().includes("VIEW"))
            .map((p) => (
              <MenuItem key={p.pageID} value={p.pageID}>
                {p.pageName}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </DialogContent>
    <DialogActions>
      <Button onClick={closeModal}>Cancel</Button>
      <Button variant="contained" onClick={handleSave}>
        Save
      </Button>
    </DialogActions>
  </Dialog>
)}

{/* Info Dialog */}
{infoRelationship && (
  <Dialog
    open={infoDialogOpen}
    onClose={() => setInfoDialogOpen(false)}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle>Relationship Details</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2">
        <strong>Created By:</strong> {infoRelationship.createdBy ?? "-"}
      </Typography>
      <Typography variant="body2">
        <strong>Created At:</strong> {infoRelationship.createdAt ?? "-"}
      </Typography>
      <Typography variant="body2">
        <strong>Updated By:</strong> {infoRelationship.updatedBy ?? "-"}
      </Typography>
      <Typography variant="body2">
        <strong>Updated At:</strong> {infoRelationship.updatedAt ?? "-"}
      </Typography>
      <Typography variant="body2">
        <strong>Deleted:</strong> {infoRelationship.deleted ? "Yes" : "No"}
      </Typography>
      {infoRelationship.deleted && (
        <>
          <Typography variant="body2">
            <strong>Deleted By:</strong> {infoRelationship.deletedBy ?? "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Deleted On:</strong> {infoRelationship.dateDeleted ?? "-"}
          </Typography>
        </>
      )}
      <Typography variant="body2">
        <strong>Row Version:</strong> {infoRelationship.rowVersion ?? "-"}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" onClick={() => setInfoDialogOpen(false)}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
)}

{searchDialogTargetRel && (
  <SearchDialog
    open={searchDialogOpen}
    onClose={() => setSearchDialogOpen(false)}
    tableId={searchDialogTargetRel.childTableID}
    fields={childFieldOptions}
    onSubmit={(filters: any) => {
      const updated = relationships.map(r =>
        r.relationshipID === searchDialogTargetRel.relationshipID
          ? { ...r, fixedFilters: filters ? JSON.stringify(filters) : "" }
          : r
      );
      setRelationships(updated);
      setTable(prev => ({ ...prev, relationships: updated }));
      setIsDirty(true);
      setSearchDialogOpen(false);
    }}    
  />
)}


    </Box>
  );
};

export default RelationshipsPanel;
